import React, { createContext, useContext, useEffect, useReducer, useState } from 'react';
import { usePricingVersion } from './PricingContext';
import { cartReducer, initialState, maxQuantityForItem } from './cart-state';

const CART_STORAGE_KEY = 'grafito_cart';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const [hydrated, setHydrated] = useState(false);
  const pricingVersion = usePricingVersion();

  // Re-sync stored prices whenever live pricing loads or changes.
  useEffect(() => {
    if (pricingVersion > 0) dispatch({ type: 'REPRICE' });
  }, [pricingVersion]);

  // Restore from localStorage on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.items) dispatch({ type: 'HYDRATE', state: parsed });
      }
    } catch {
      // Ignore corrupt storage
    }
    setHydrated(true);
  }, []);

  // Persist to localStorage on change (not before the stored basket is restored)
  useEffect(() => {
    if (typeof window === 'undefined' || !hydrated) return;
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Ignore storage errors
    }
  }, [state, hydrated]);

  const itemCount = state.items.reduce((sum, i) => sum + i.quantity, 0);

  const value = {
    cart: state,
    items: state.items,
    itemCount,
    hydrated,
    maxQuantityFor: maxQuantityForItem,
    buyNow: (product, options = {}) =>
      dispatch({
        type: 'BUY_NOW',
        product,
        quantity: options.quantity ?? 1,
        variant: options.variant || null,
        casing: options.casing || null,
      }),
    removeItem: (key) => dispatch({ type: 'REMOVE_ITEM', key }),
    updateQuantity: (key, quantity) => dispatch({ type: 'UPDATE_QUANTITY', key, quantity }),
    clearCart: () => dispatch({ type: 'CLEAR_CART' }),
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within a CartProvider');
  return ctx;
}
